import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { DishSelection } from "@admin/components/molecules/SelectWithAdd/DishSelection";
import {
  addFoodProductionControl,
  dateToISO,
  FoodControlPointInstance,
  FoodProductionControl,
  InstanceStatus,
  parseRemarkAndCorrectiveActions,
  updateFoodProductionControl,
  UpsertModalType,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { UpsertProcessingProductModal } from "./UpsertProcessingProductModal";

interface RefrigerationModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  initialData?: FoodProductionControl;
}

export function RefrigerationModal({
  foodControlPointInstance,
  initialData,
  ...props
}: RefrigerationModalProps) {
  const { t } = useTranslation();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState<boolean>(false);
  const [isAddDishModalOpen, setIsAddDishModalOpen] = useState(false);
  const [isRemarksNeeded, setIsRemarksNeeded] = useState(false);
  const [note, setNote] = useState<string[]>([]);

  const addProductionControlSchema = z.object({
    startTime: z.string().min(1, t("food.Start Time is Required")),
    endTime: z.string().min(1, t("food.End Time is Required")),
    startTemperature: z
      .string()
      .min(1, t("food.Start Temperature is Required")),
    finalTemperature: z
      .string()
      .min(1, t("food.Final Temperature is Required")),
    remarks: z
      .string()
      .optional()
      .refine((e) => !isRemarksNeeded, {
        message: t("food.Remarks are mandatory"),
      }),
    dishName: z.string().min(1, t("food.Dish name is required")),
  });
  type AddProductionControlData = z.infer<typeof addProductionControlSchema>;

  const processHours = (startTime: string, endTime: string) => {
    const startDate = new Date();
    const endDate = new Date();
    const [startHours, startMins] = getTimeInHourMinSec(startTime);
    const [endHours, endMins] = getTimeInHourMinSec(endTime);
    startDate.setHours(startHours, startMins);
    endDate.setHours(endHours, endMins);

    return [dateToISO(startDate), dateToISO(endDate)];
  };

  const getTimeInHourMinSec = (time: string): number[] => {
    if (!time) return [0, 0];
    const [hour, minute] = time.split(":").map(Number);
    return [hour, minute];
  };

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    reset,
  } = useForm<AddProductionControlData>({
    resolver: zodResolver(addProductionControlSchema),
  });

  const updateMutation = useMutation({
    mutationFn: updateFoodProductionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Food Refrigeration Added"), { duration: 4000 });
    },
    onError: () => {
      toast.error(t("food.Unable to add Food Refrigeration"));
    },
  });

  const mutation = useMutation({
    mutationFn: addFoodProductionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Food Refrigeration Added"), { duration: 4000 });
    },
    onError: () => {
      toast.error(t("food.Unable to add Food Refrigeration"));
    },
  });

  const onSubmit = (data: AddProductionControlData) => {
    const [startDate, endDate] = processHours(data.startTime, data.endTime);

    const isCompeletedWithErrors = isCorrectiveActionNeeded;

    const payload = {
      companyId: foodControlPointInstance.companyId,
      companyLocationId: foodControlPointInstance.companyLocationId,
      startDateTime: startDate,
      endDateTime: endDate,
      startTemperature: parseInt(data.startTemperature),
      finalTemperature: parseInt(data.finalTemperature),
      reason: JSON.stringify(note),
      remark: data.remarks,
      dishName: data.dishName,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      controlPointInstanceId: foodControlPointInstance.id,
      foodControlPointInstance: foodControlPointInstance,
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }

    mutation.mutate(payload);
  };

  const noteTexts = {
    note1: t("food.Product Discarded"),
    note2: t("food.Product Reheated Cooling"),
    note3: t("food.Food Cooling Other"),
  };

  const editNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = noteTexts[e.target.name as keyof typeof noteTexts];
    const isChecked = e.target.checked;
    isChecked
      ? setNote((prev) => [...prev, noteText])
      : setNote((prev) => prev.filter((note) => note !== noteText));
    if (e.target.name === "note3") {
      setIsRemarksNeeded(isChecked);
    }
  };

  const handleOnClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (initialData) {
      const { remark, reason } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );

      reset({
        remarks: remark,
        dishName: initialData.dishName,
        startTemperature: initialData.startTemperature?.toString(),
        finalTemperature: initialData.finalTemperature?.toString(),
        startTime: initialData.startDateTime
          ? format(initialData.startDateTime, "HH:mm")
          : "",
        endTime: initialData.endDateTime
          ? format(initialData.endDateTime, "HH:mm")
          : "",
      });

      setNote(reason);

      if (reason.length > 0) {
        setIsCorrectiveActionNeeded(true);
      } else {
        setIsCorrectiveActionNeeded(false);
      }
    }
  }, [initialData, reset, setValue]);

  return (
    <>
      <Modal
        actionButtons={
          <>
            <Button
              variant="secondary"
              onClick={handleOnClose}
              disabled={mutation.isPending}
            >
              {t("common.Discard")}
            </Button>
            <Button
              loading={mutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {t("food.Submit Entry")}
            </Button>
          </>
        }
        {...props}
        title={t("food.Food Refrigeration")}
      >
        <form>
          <Label required title={t("food.Name of the Product")}>
            <div className="flex flex-col gap-y-1 w-full">
              <DishSelection
                companyId={foodControlPointInstance.companyId}
                onAddNew={() => setIsAddDishModalOpen(true)}
                onChange={(e) => setValue("dishName", e.target.name)}
                defaultValue={initialData?.dishName}
              />
              <ErrorMessage message={errors?.dishName?.message} />
            </div>
          </Label>
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <div className="flex flex-col gap-y-6">
            <div className="flex flex-row items-center">
              <label className="form-label flex items-center gap-1  !font-normal">
                {t("food.AfterFourHours Lower")}
                <span className="text-red-500">*</span>
              </label>
              <input
                className={`radio radio-lg ${!isCorrectiveActionNeeded && "!bg-[#296573] !border-[#296573]"}`}
                onChange={(e) => {
                  setIsCorrectiveActionNeeded(false);
                  setIsRemarksNeeded(false);
                  setNote([]);
                }}
                name="temperature"
                type="radio"
                checked={!isCorrectiveActionNeeded}
              />
            </div>
            <div className="flex flex-row items-center">
              <label className="form-label flex items-center gap-1  !font-normal">
                {t("food.AfterFourHours Higher")}
                <span className="text-red-500">*</span>
              </label>
              <input
                className={`radio radio-lg ${isCorrectiveActionNeeded && "!bg-[#8D4F61] !border-[#8D4F61]"}`}
                onChange={(e) => {
                  setIsCorrectiveActionNeeded(true);
                }}
                name="temperature"
                type="radio"
                defaultChecked={isCorrectiveActionNeeded}
              />
            </div>
          </div>

          <div className="border-b border-b-gray-200 my-2.5"></div>
          <Label required title={t("food.Cooling Start Time")}>
            <div className="flex flex-col items-end w-full gap-2">
              <Input {...register("startTime")} type="time" />
              <ErrorMessage message={errors?.startTime?.message} />
            </div>
          </Label>
          <Label required title={t("food.Cooling Start Temperature")}>
            <div className="flex flex-col items-end w-full gap-2">
              <div className="flex flex-row justify-between w-full gap-2 items-center">
                <Input {...register("startTemperature")} type="number" />
                <span className="text-gray-700">°C</span>
              </div>
              <ErrorMessage message={errors?.startTemperature?.message} />
            </div>
          </Label>
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <Label required title={t("food.Cooling End Time")}>
            <div className="flex flex-col items-end w-full gap-2">
              <Input {...register("endTime")} type="time" />
              <ErrorMessage message={errors?.endTime?.message} />
            </div>
          </Label>
          <Label required title={t("food.Cooling End Temperature")}>
            <div className="flex flex-col items-end w-full gap-2">
              <div className="flex flex-row justify-between w-full gap-2 items-center">
                <Input {...register("finalTemperature")} type="number" />
                <span className="text-gray-700">°C</span>
              </div>
              <ErrorMessage message={errors?.finalTemperature?.message} />
            </div>
          </Label>
          {isCorrectiveActionNeeded && (
            <>
              <div className="border-b border-b-gray-200 my-2.5"></div>
              <Label required title={t("food.Corrective Action")}>
                <div className="flex flex-col w-full gap-2 ">
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note1"
                      className="checkbox checkbox-md"
                      type="checkbox"
                      onChange={(e) => editNote(e)}
                      defaultChecked={note.includes(noteTexts.note1)}
                    />
                    {noteTexts.note1}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      onChange={(e) => editNote(e)}
                      className="checkbox checkbox-md"
                      type="checkbox"
                      name="note2"
                      defaultChecked={note.includes(noteTexts.note2)}
                    />
                    {noteTexts.note2}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note3"
                      className="checkbox checkbox-md"
                      type="checkbox"
                      defaultChecked={note.includes(noteTexts.note3)}
                      onChange={(e) => editNote(e)}
                    />
                    {noteTexts.note3}
                  </div>
                </div>
              </Label>
            </>
          )}
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <Label title={t("food.Remarks")}>
            <div className="flex flex-col items-end w-full gap-2 ">
              <Input
                {...register("remarks")}
                showRequired={isRemarksNeeded}
                placeholder="-"
                onChange={(e) => {
                  setIsRemarksNeeded(!e.target.value);
                }}
              />
              <ErrorMessage message={errors?.remarks?.message} />
            </div>
          </Label>
        </form>
      </Modal>
      <UpsertProcessingProductModal
        open={isAddDishModalOpen}
        onClose={() => setIsAddDishModalOpen(false)}
        companyId={foodControlPointInstance.companyId}
        selectedLocationId={foodControlPointInstance.companyLocationId}
        type={UpsertModalType.Add}
      />
    </>
  );
}
